import { parse as dateParse, addDays, isBefore, startOfDay, getDay, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { DateMatch } from './command-parser.model';
import { levenshtein } from './levenshtein';

/**
 * Costruisce una data locale validando che il giorno ESISTA nel mese indicato.
 * `new Date(y, m, d)` in JS fa rollover silenzioso (es. 31 febbraio → 3 marzo): questo
 * helper lo rileva confrontando giorno/mese risultanti con quelli richiesti e ritorna
 * `null` se non combaciano (giorno inesistente). `month` è 0-based; sono ammessi mesi
 * "fuori range" (es. 12 = gennaio anno dopo) purché il giorno esista dopo la normalizzazione.
 * Vedi change fix-date-parser-invalid-day.
 */
function costruisciDataValida(year: number, month: number, day: number): Date | null {
  const d = new Date(year, month, day);
  // Mese atteso dopo la normalizzazione JS di un mese eventualmente fuori range [0,11].
  const meseAtteso = ((month % 12) + 12) % 12;
  if (d.getDate() !== day || d.getMonth() !== meseAtteso) {
    return null;
  }
  return d;
}

const RELATIVE_DATES: Record<string, (today: Date) => Date> = {
  'oggi': (today) => today,
  'domani': (today) => addDays(today, 1),
  'dopodomani': (today) => addDays(today, 2),
};

const DAY_NAMES: Record<string, number> = {
  'lunedi': 1, 'lunedì': 1,
  'martedi': 2, 'martedì': 2,
  'mercoledi': 3, 'mercoledì': 3,
  'giovedi': 4, 'giovedì': 4,
  'venerdi': 5, 'venerdì': 5,
  'sabato': 6,
  'domenica': 0,
};

const MONTH_NAMES: Record<string, number> = {
  'gennaio': 0, 'febbraio': 1, 'marzo': 2, 'aprile': 3,
  'maggio': 4, 'giugno': 5, 'luglio': 6, 'agosto': 7,
  'settembre': 8, 'ottobre': 9, 'novembre': 10, 'dicembre': 11,
};

const MIN_FUZZY_LENGTH = 5;
const MAX_FUZZY_DISTANCE = 1;

/**
 * Fuzzy-matches a token against a dictionary of keywords.
 * Only activates for tokens >= 5 chars to avoid false positives on short words like "oggi".
 * Returns the matched key or null.
 */
function fuzzyMatchKey<T>(token: string, dict: Record<string, T>): string | null {
  if (token.length < MIN_FUZZY_LENGTH) return null;

  let bestKey: string | null = null;
  let bestDist = MAX_FUZZY_DISTANCE + 1;

  for (const key of Object.keys(dict)) {
    if (key.length < MIN_FUZZY_LENGTH) continue;
    const dist = levenshtein(token, key);
    if (dist <= MAX_FUZZY_DISTANCE && dist < bestDist) {
      bestDist = dist;
      bestKey = key;
    }
  }

  return bestKey;
}

export interface DateParseResult {
  match: DateMatch;
  consumedIndices: number[];
  rawTokens: string[];
}

export function parseDate(tokens: string[], validate: boolean): DateParseResult | null {
  const today = startOfDay(new Date());
  return parseDateFromTokens(tokens, today, validate);
}

export interface ExtractDatesResult {
  dateResult: DateParseResult;
  textWithPlaceholder: string;
}

/**
 * Extracts dates from lossless normalized text (before stopword removal).
 * Returns the parsed date and the text with the matched segment replaced by __DATE__.
 */
export function extractDates(rawNormalized: string, validate: boolean): ExtractDatesResult | null {
  const tokens = rawNormalized.split(' ').filter(t => t.length > 0);
  const today = startOfDay(new Date());

  const dateResult = parseDateFromTokens(tokens, today, validate);
  if (!dateResult) return null;

  // Build text with the matched segment replaced by __DATE__
  const minIdx = Math.min(...dateResult.consumedIndices);
  const maxIdx = Math.max(...dateResult.consumedIndices);
  const resultTokens = [
    ...tokens.slice(0, minIdx),
    '__DATE__',
    ...tokens.slice(maxIdx + 1),
  ];

  return {
    dateResult,
    textWithPlaceholder: resultTokens.join(' '),
  };
}

function parseDateFromTokens(tokens: string[], today: Date, validate: boolean): DateParseResult | null {
  // Try range first: "dal X al Y"
  const rangeResult = tryParseRange(tokens, today, validate);
  if (rangeResult) return rangeResult;

  // Try conjunction: "oggi e domani", "14 e 15 ottobre"
  const conjunctionResult = tryParseConjunction(tokens, today, validate);
  if (conjunctionResult) return conjunctionResult;

  // Try duration: "per 3 giorni", "per una settimana"
  const durationResult = tryParseDuration(tokens, today, validate);
  if (durationResult) return durationResult;

  // Try shorthand range: "14-18 ottobre"
  const shorthandResult = tryParseShorthandRange(tokens, today, validate);
  if (shorthandResult) return shorthandResult;

  // Try single date
  for (let i = 0; i < tokens.length; i++) {
    const single = resolveSingleDate(tokens, i, today, validate);
    if (single) {
      return {
        match: { inizio: single.date },
        consumedIndices: single.consumedIndices,
        rawTokens: single.rawTokens,
      };
    }
  }

  return null;
}

interface ResolvedSingleDate {
  date: Date;
  consumedIndices: number[];
  rawTokens: string[];
  hasMonth: boolean;
}

/**
 * Tries to resolve a single date starting at the given index.
 * Handles relative dates, day-of-week, and date-with-context (14 ottobre, 14/10, etc.).
 */
function resolveSingleDate(
  tokens: string[], idx: number, today: Date, validate: boolean
): ResolvedSingleDate | null {
  const token = tokens[idx];

  // Skip multi-word placeholders
  if (token.startsWith('__')) return null;

  // Relative dates: "oggi", "domani", "dopodomani"
  if (RELATIVE_DATES[token]) {
    const date = RELATIVE_DATES[token](today);
    if (validate && isBefore(date, today)) return null;
    return { date, consumedIndices: [idx], rawTokens: [token], hasMonth: false };
  }

  // Day of week: "lunedì", "martedi", etc.
  if (DAY_NAMES[token] !== undefined) {
    const date = getNextDayOfWeek(today, DAY_NAMES[token]);
    return { date, consumedIndices: [idx], rawTokens: [token], hasMonth: false };
  }

  // Fuzzy match on relative dates (e.g., "doman" → "domani")
  const fuzzyRelative = fuzzyMatchKey(token, RELATIVE_DATES);
  if (fuzzyRelative) {
    const date = RELATIVE_DATES[fuzzyRelative](today);
    if (validate && isBefore(date, today)) return null;
    return { date, consumedIndices: [idx], rawTokens: [token], hasMonth: false };
  }

  // Fuzzy match on day names (e.g., "mercoled" → "mercoledì")
  const fuzzyDay = fuzzyMatchKey(token, DAY_NAMES);
  if (fuzzyDay) {
    const date = getNextDayOfWeek(today, DAY_NAMES[fuzzyDay]);
    return { date, consumedIndices: [idx], rawTokens: [token], hasMonth: false };
  }

  // "14 ottobre" or "14/10" or "14/10/2026"
  const dateCtx = tryParseDateWithContext(tokens, idx, today, validate);
  if (dateCtx) {
    const hasMonth = dateCtx.consumedIndices.length > 1
      || token.includes('/') || token.includes('-');
    return {
      date: dateCtx.match.inizio,
      consumedIndices: dateCtx.consumedIndices,
      rawTokens: dateCtx.rawTokens,
      hasMonth,
    };
  }

  return null;
}

const DURATION_UNITS: Record<string, number> = {
  'giorno': 1, 'giorni': 1,
  'settimana': 7, 'settimane': 7,
};

const TEXT_NUMBERS: Record<string, number> = {
  'un': 1, 'una': 1,
};

function tryParseConjunction(tokens: string[], today: Date, validate: boolean): DateParseResult | null {
  for (let eIdx = 1; eIdx < tokens.length - 1; eIdx++) {
    if (tokens[eIdx] !== 'e') continue;

    // Try to resolve the date immediately before "e"
    // Start from the earliest possible position (up to 2 tokens before "e" for "14 ottobre e ...")
    let left: ResolvedSingleDate | null = null;
    let leftStart = eIdx - 1;

    // Try 2-token left side first (e.g., "14 ottobre"), then 1-token
    if (eIdx >= 2) {
      left = resolveSingleDate(tokens, eIdx - 2, today, false);
      if (left && left.consumedIndices[left.consumedIndices.length - 1] === eIdx - 1) {
        leftStart = eIdx - 2;
      } else {
        left = null;
      }
    }
    if (!left) {
      left = resolveSingleDate(tokens, eIdx - 1, today, false);
      if (left) {
        leftStart = eIdx - 1;
      }
    }
    if (!left) continue;

    // Try to resolve the date immediately after "e"
    const right = resolveSingleDate(tokens, eIdx + 1, today, false);
    if (!right) continue;

    const finalStart = left.date;
    const finalEnd = right.date;

    // If right side has no month, inherit from left
    if (!right.hasMonth && left.hasMonth) {
      finalEnd.setMonth(finalStart.getMonth());
      if (finalEnd.getDate() < finalStart.getDate()) {
        finalEnd.setMonth(finalEnd.getMonth() + 1);
      }
    }

    if (validate && isBefore(finalStart, today)) continue;

    const allIndices: number[] = [];
    const allRawTokens: string[] = [];
    for (let i = leftStart; i <= right.consumedIndices[right.consumedIndices.length - 1]; i++) {
      allIndices.push(i);
      allRawTokens.push(tokens[i]);
    }

    return {
      match: { inizio: finalStart, fine: finalEnd },
      consumedIndices: allIndices,
      rawTokens: allRawTokens,
    };
  }
  return null;
}

function tryParseDuration(tokens: string[], today: Date, validate: boolean): DateParseResult | null {
  // Pattern 1: "per 3 giorni", "per una settimana"
  for (let perIdx = 0; perIdx < tokens.length - 2; perIdx++) {
    if (tokens[perIdx] !== 'per') continue;

    const result = tryMatchDuration(tokens, perIdx + 1, today, validate, [perIdx]);
    if (result) return result;
  }

  // Pattern 2: "3 giorni", "una settimana" (without "per")
  for (let i = 0; i < tokens.length - 1; i++) {
    if (tokens[i].startsWith('__')) continue;
    const result = tryMatchDuration(tokens, i, today, validate, []);
    if (result) return result;
  }

  return null;
}

function tryMatchDuration(
  tokens: string[], numIdx: number, today: Date, validate: boolean, prefixIndices: number[]
): DateParseResult | null {
  if (numIdx + 1 >= tokens.length) return null;

  const numToken = tokens[numIdx];
  let n: number;

  if (TEXT_NUMBERS[numToken] !== undefined) {
    n = TEXT_NUMBERS[numToken];
  } else {
    n = parseInt(numToken, 10);
    if (isNaN(n)) return null;
  }

  if (n < 1) return null;

  const unitToken = tokens[numIdx + 1];
  const multiplier = DURATION_UNITS[unitToken];
  if (multiplier === undefined) return null;

  const totalDays = n * multiplier;
  const startDate = today;
  const endDate = addDays(today, totalDays - 1);

  if (validate && isBefore(startDate, today)) return null;

  const consumedIndices = [...prefixIndices, numIdx, numIdx + 1];
  const rawTokens = consumedIndices.map(i => tokens[i]);

  return {
    match: { inizio: startDate, fine: endDate },
    consumedIndices,
    rawTokens,
  };
}

function tryParseRange(tokens: string[], today: Date, validate: boolean): DateParseResult | null {
  // Find "dal" ... "al" pattern
  const dalIdx = tokens.indexOf('dal');
  const daIdx = dalIdx === -1 ? tokens.indexOf('da') : -1;
  const startIdx = dalIdx !== -1 ? dalIdx : daIdx;
  if (startIdx === -1) return null;

  const alIdx = tokens.indexOf('al', startIdx + 1);
  const aIdx = alIdx === -1 ? tokens.indexOf('a', startIdx + 1) : -1;
  const endIdx = alIdx !== -1 ? alIdx : aIdx;
  if (endIdx === -1) return null;

  // Tokens between dal and al are the start date
  const startTokens = tokens.slice(startIdx + 1, endIdx);
  // Tokens after al are the end date
  const endTokens = tokens.slice(endIdx + 1);

  // Caso "da <giorno> a <giorno>" (es. "da lunedì a venerdì"): entrambi gli estremi sono
  // NOMI di giorno, non date numeriche. `resolveDateTokens` risolve ciascun giorno rispetto
  // a "oggi" indipendentemente: se il giorno di fine cade PRIMA di quello di inizio nella
  // stessa settimana (es. oggi e' giovedì: "lunedì" salta alla settimana prossima, "venerdì"
  // resta in quella corrente), la fine precede l'inizio e la logica di inherit-mese sotto
  // — pensata per date numeriche tipo "dal 28 al 3" — la spinge di un mese intero, producendo
  // un intervallo di ~4 settimane invece dei pochi giorni attesi. Il fix: quando ENTRAMBI gli
  // estremi sono nomi di giorno, la fine si risolve rispetto all'INIZIO gia' calcolato (prossima
  // occorrenza di quel giorno a partire dall'inizio), non rispetto a "oggi": cosi' l'intervallo
  // resta sempre coerente (la stessa settimana, o quella immediatamente successiva) qualunque
  // sia il giorno della settimana in cui gira il parser.
  const startWeekday = startTokens.length === 1 ? resolveWeekdayToken(startTokens[0]) : null;
  const endWeekday = endTokens.length === 1 ? resolveWeekdayToken(endTokens[0]) : null;
  if (startWeekday !== null && endWeekday !== null) {
    const finalStart = getNextDayOfWeek(today, startWeekday);
    const finalEnd = getNextDayOfWeek(finalStart, endWeekday);

    if (validate && isBefore(finalStart, today)) return null;

    const allIndices: number[] = [];
    for (let i = startIdx; i < Math.min(tokens.length, endIdx + 1 + endTokens.length); i++) {
      allIndices.push(i);
    }
    const allRawTokens = tokens.slice(startIdx, endIdx + 1 + endTokens.length);

    return {
      match: { inizio: finalStart, fine: finalEnd },
      consumedIndices: allIndices,
      rawTokens: allRawTokens,
    };
  }

  const startDate = resolveDateTokens(startTokens, today);
  const endDate = resolveDateTokens(endTokens, today);

  if (!startDate || !endDate) return null;

  // If end date has no month, inherit from start
  const finalEnd = endDate.date;
  const finalStart = startDate.date;

  // If end month not explicit and end day < start day, assume next month
  if (!endDate.hasMonth && finalEnd.getDate() < finalStart.getDate()) {
    finalEnd.setMonth(finalEnd.getMonth() + 1);
  }

  // Inherit month from start if end has no month
  if (!endDate.hasMonth && startDate.hasMonth) {
    finalEnd.setMonth(finalStart.getMonth());
    if (finalEnd.getDate() < finalStart.getDate()) {
      finalEnd.setMonth(finalEnd.getMonth() + 1);
    }
  }

  if (validate && isBefore(finalStart, today)) return null;

  const allIndices: number[] = [];
  for (let i = startIdx; i < Math.min(tokens.length, endIdx + 1 + endTokens.length); i++) {
    allIndices.push(i);
  }

  const allRawTokens = tokens.slice(startIdx, endIdx + 1 + endTokens.length);

  return {
    match: { inizio: finalStart, fine: finalEnd },
    consumedIndices: allIndices,
    rawTokens: allRawTokens,
  };
}

function tryParseShorthandRange(tokens: string[], today: Date, validate: boolean): DateParseResult | null {
  // "14-18 ottobre" or "14-18"
  for (let i = 0; i < tokens.length; i++) {
    const match = tokens[i].match(/^(\d{1,2})-(\d{1,2})$/);
    if (!match) continue;

    const dayStart = parseInt(match[1], 10);
    const dayEnd = parseInt(match[2], 10);
    if (dayStart < 1 || dayStart > 31 || dayEnd < 1 || dayEnd > 31) continue;

    let month: number | null = null;
    let year = today.getFullYear();
    const consumedIndices = [i];
    const rawTokens = [tokens[i]];

    // Check if next token is a month name (exact or fuzzy)
    const shorthandMonthKey = i + 1 < tokens.length
      ? (MONTH_NAMES[tokens[i + 1]] !== undefined ? tokens[i + 1] : fuzzyMatchKey(tokens[i + 1], MONTH_NAMES))
      : null;
    if (shorthandMonthKey) {
      month = MONTH_NAMES[shorthandMonthKey];
      consumedIndices.push(i + 1);
      rawTokens.push(tokens[i + 1]);
    }

    if (month === null) {
      month = today.getMonth();
      // If day already passed this month, use next month
      if (dayStart < today.getDate()) month++;
    }

    const startDate = costruisciDataValida(year, month, dayStart);
    const endDate = costruisciDataValida(year, dayEnd < dayStart ? month + 1 : month, dayEnd);
    // Giorno inesistente su un estremo (es. "30-31 febbraio") → nessun range proposto.
    if (!startDate || !endDate) continue;

    if (validate && isBefore(startDate, today)) return null;

    return {
      match: { inizio: startDate, fine: endDate },
      consumedIndices,
      rawTokens,
    };
  }
  return null;
}

function tryParseDateWithContext(
  tokens: string[], idx: number, today: Date, validate: boolean
): DateParseResult | null {
  const token = tokens[idx];

  // "14/10/2026" or "14/10"
  const slashMatch = token.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/);
  if (slashMatch) {
    const day = parseInt(slashMatch[1], 10);
    const month = parseInt(slashMatch[2], 10) - 1;
    const year = slashMatch[3] ? parseInt(slashMatch[3], 10) : today.getFullYear();
    const date = costruisciDataValida(year, month, day);
    if (!date) return null; // es. "31/02" → giorno inesistente
    if (validate && isBefore(date, today)) return null;
    return { match: { inizio: date }, consumedIndices: [idx], rawTokens: [token] };
  }

  // "14-10-2026" or "14-10" (dash format, not range — single token with dashes)
  const dashMatch = token.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const day = parseInt(dashMatch[1], 10);
    const month = parseInt(dashMatch[2], 10) - 1;
    const year = parseInt(dashMatch[3], 10);
    const date = costruisciDataValida(year, month, day);
    if (!date) return null; // es. "31-04-2026" → giorno inesistente
    if (validate && isBefore(date, today)) return null;
    return { match: { inizio: date }, consumedIndices: [idx], rawTokens: [token] };
  }

  // Pure number followed by month name: "14 ottobre"
  const dayMatch = token.match(/^(\d{1,2})$/);
  if (dayMatch) {
    const day = parseInt(dayMatch[1], 10);
    if (day < 1 || day > 31) return null;

    // Check if next token is a month name (exact or fuzzy)
    const nextToken = idx + 1 < tokens.length ? tokens[idx + 1] : undefined;
    const monthKey = nextToken !== undefined
      ? (MONTH_NAMES[nextToken] !== undefined ? nextToken : fuzzyMatchKey(nextToken, MONTH_NAMES))
      : null;
    if (monthKey) {
      const month = MONTH_NAMES[monthKey];
      const year = today.getFullYear();
      let date = costruisciDataValida(year, month, day);
      if (!date) return null; // es. "31 febbraio" → giorno inesistente
      // If date is in the past, use next year
      if (validate && isBefore(date, today)) {
        const dateNext = costruisciDataValida(year + 1, month, day);
        // 29 febbraio non bisestile l'anno dopo → nessuna data valida.
        if (!dateNext || isBefore(dateNext, today)) return null;
        date = dateNext;
      }
      return {
        match: { inizio: date },
        consumedIndices: [idx, idx + 1],
        rawTokens: [token, tokens[idx + 1]],
      };
    }

    // Standalone number — could be a day in current/next month
    let month = today.getMonth();
    const year = today.getFullYear();
    let date = costruisciDataValida(year, month, day);
    if (date && validate && isBefore(date, today)) {
      month++;
      date = costruisciDataValida(year, month, day);
    }
    // Match solo se il giorno esiste nel mese risolto (es. "31" a novembre → null).
    if (date) {
      return { match: { inizio: date }, consumedIndices: [idx], rawTokens: [token] };
    }
  }

  return null;
}

interface ResolvedDate {
  date: Date;
  hasMonth: boolean;
}

function resolveDateTokens(tokens: string[], today: Date): ResolvedDate | null {
  if (tokens.length === 0) return null;

  // "domani", "oggi", etc. (exact + fuzzy)
  if (tokens.length === 1) {
    const relKey = RELATIVE_DATES[tokens[0]] ? tokens[0] : fuzzyMatchKey(tokens[0], RELATIVE_DATES);
    if (relKey) {
      return { date: RELATIVE_DATES[relKey](today), hasMonth: false };
    }
  }

  // Day of week (exact + fuzzy)
  if (tokens.length === 1) {
    const dayKey = DAY_NAMES[tokens[0]] !== undefined ? tokens[0] : fuzzyMatchKey(tokens[0], DAY_NAMES);
    if (dayKey) {
      return { date: getNextDayOfWeek(today, DAY_NAMES[dayKey]), hasMonth: false };
    }
  }

  // "14 ottobre" or just "14"
  const dayStr = tokens[0];
  const dayMatch = dayStr.match(/^(\d{1,2})$/);
  if (!dayMatch) return null;

  const day = parseInt(dayMatch[1], 10);
  if (day < 1 || day > 31) return null;

  let month = today.getMonth();
  let hasMonth = false;
  const year = today.getFullYear();

  const monthToken = tokens.length > 1 ? tokens[1] : undefined;
  const monthMatchKey = monthToken !== undefined
    ? (MONTH_NAMES[monthToken] !== undefined ? monthToken : fuzzyMatchKey(monthToken, MONTH_NAMES))
    : null;
  if (monthMatchKey) {
    month = MONTH_NAMES[monthMatchKey];
    hasMonth = true;
  } else {
    // No month specified — default to current month, if passed use next
    if (day < today.getDate()) month++;
  }

  const date = costruisciDataValida(year, month, day);
  if (!date) return null; // giorno inesistente nel mese (es. 31 febbraio)
  return { date, hasMonth };
}

/** Numero del giorno (0=domenica..6=sabato) per un token "lunedì"/"lunedi", exact + fuzzy; `null` se non e' un nome di giorno. */
function resolveWeekdayToken(token: string): number | null {
  if (DAY_NAMES[token] !== undefined) return DAY_NAMES[token];
  const fuzzyDay = fuzzyMatchKey(token, DAY_NAMES);
  return fuzzyDay !== null ? DAY_NAMES[fuzzyDay] : null;
}

function getNextDayOfWeek(today: Date, targetDay: number): Date {
  const currentDay = getDay(today);
  let daysAhead = targetDay - currentDay;
  if (daysAhead <= 0) daysAhead += 7;
  return addDays(today, daysAhead);
}

export function formatDateForDisplay(date: Date): string {
  return format(date, 'd MMMM yyyy', { locale: it });
}

export function formatDateShort(date: Date): string {
  return format(date, 'd MMM', { locale: it });
}
